/* eslint-disable no-await-in-loop */
import {
  useAppDispatch,
  useAppSelector,
  useStructureSaveQueue
} from 'app/hooks';
import store from 'app/store';
import {
  AirHandlerTypeCodeType,
  ConcealedDuctLocationType,
  CondenserType,
  ConfigurationType,
  LevelType,
  SectionType,
  SpaceType,
  SystemSelectorInstanceType,
  ZoneType
} from 'data/types';
import { detailedDiff } from 'deep-object-diff';
import {
  useDeleteConfigurationAirHandlersMutation,
  useDeleteConfigurationCondensersMutation,
  useDeleteConfigurationLineSetsMutation,
  useDeleteConfigurationMutation,
  useDeleteConfigurationZonesMutation,
  useDeleteLevelMutation,
  useDeleteSectionMutation,
  useDeleteSpaceMutation,
  useGenerateSizingItemsAndAttributesMutation,
  useGenerateWindowsMutation,
  useSaveBaseStructureMutation,
  useSaveConfigurationAirHandlersMutation,
  useSaveConfigurationCondensersMutation,
  useSaveConfigurationLineSetsMutation,
  useSaveConfigurationMutation,
  useSaveConfigurationZonesMutation,
  useSaveInstanceMutation,
  useSaveLevelsMutation,
  useSaveSectionsMutation,
  useSaveSpacesMutation
} from 'features/api/project-api';
import {
  markConfigSaveCompleted,
  markConfigSaveRequested,
  markLocallyCreatedConfigurationIdsSaved,
  setConfigSavedAt
} from 'features/configurations';
import { bumpSettingsSaveVersion, setInstanceUniqueId } from 'features/project';
import {
  bumpEquipmentPersistVersion,
  bumpEquipmentSaveVersion,
  markStructureSaveCompleted,
  markStructureSaveRequested,
  saveStructureSpecificSizingItemDefaults,
  setHybridAutobuildWindowsPending,
  setIsNewlyCreatedStructure,
  setStructureOptions,
  setStructureUniqueId
} from 'features/structure';
import { nanoid } from 'nanoid';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import axios from 'utils/api';
import { truncateAttributes } from 'utils/attributes';
import {
  CoalescingAsyncRunner,
  createCoalescingAsyncRunner
} from 'utils/coalescing-async-runner';
import {
  detectConfigurationChanges,
  transformCondensersForDatabase,
  transformConfigurationForDatabase,
  transformLineSetsForDatabase
} from 'utils/configuration-db';
import { refetchStructureSpaces } from 'utils/data';
import { buildEquipmentDerivationKey } from 'utils/existing-equipment';
import { setQueryParam } from 'utils/helpers';
import { HYBRID_AUTOBUILD_TUNABLES } from 'utils/hybrid-structure-autobuild';
import { isLinesetDragActive } from 'utils/lineset-drag-state';
import { isHybridOnlyPreference } from 'utils/project';
import { countChangedLevelTypes } from 'utils/count-changed-level-types';
import { shouldGenerateSizingAfterStructureSave } from 'utils/should-generate-sizing-after-structure-save';
import {
  spaceSaveAirHandlerRankings,
  spaceSaveBTUs
} from 'utils/space-save-defaults';
import {
  spacesOnChangedLevels,
  uniqueSpacesById
} from 'utils/spaces-on-changed-levels';
import { zoneTypeCodeForSave } from 'utils/resolve-zone-type';
import {
  areAllDefaultSizingItemsValid,
  seedDefaultSizingItemInsulationValues
} from 'utils/sizing';
import {
  classifyStructureItemIds,
  clearStructureSaveFailures,
  collectUnpersistedParentSections,
  mergeItemsIntoStructureBaseline,
  mergeSpacesIntoStructureBaseline,
  omitItemsFromStructureBaseline,
  quarantineFailedSaveIds,
  spaceUniqueIdsWithAbandonedParents,
  type StructureItemIds
} from 'utils/structure-autosave';
import {
  BLOCKING_SAVE_FAILURE_TOAST,
  describeStructureSaveFailure
} from 'utils/structure-save-error';
import { sanitizeSystemsToCompare } from 'utils/sanitize-system-option';
import { stripQuoteOnlyFields } from 'utils/selected-system-signature';
import { truncateSelectedSystems } from 'utils/systems';
import { notification } from 'utils/ui';
import { serializeZoneDuctModes } from 'utils/zone-duct-modes';
import { createSaveTracker, SaveTracker } from './save-tracker';

type AutoSaveWatcherProps = {
  loading: boolean;
};

export default function AutoSaveWatcher({ loading }: AutoSaveWatcherProps) {
  const dispatch = useAppDispatch();
  const {
    instanceUniqueId,
    projectDetails,
    attomData,
    projectSettings,
    locationData,
    propertySummary
  } = useAppSelector((state) => state.project);
  const { configurations } = useAppSelector((state) => state.configurations);
  const {
    structureUniqueId,
    structureName,
    structure,
    structureTypeId,
    structureSubtypeId,
    partialStructure,
    existingSystems,
    notes,
    footprint,
    condenserLocations
  } = useAppSelector((state) => state.structure);
  const { selectedSystems, selectedDuctedSystems, systemsToCompare } =
    useAppSelector((state) => state.systems);
  const { systemFilters, zoneDuctModes } = useAppSelector((state) => state.ui);
  const { defaultSizingItems, defaultSizingItemsVersion } = useAppSelector(
    (state) => state.data
  );

  const previousDefaultSizingItemsRef = useRef(defaultSizingItems);

  const autoGenerateSizingItems =
    projectSettings.find((ps) => ps.code === 'auto_generate_structure_sizing')
      ?.value !== 'No';
  const autoApplySpaceAboveAndBelow =
    projectSettings.find((ps) => ps.code === 'auto_apply_space_above_and_below')
      ?.value !== 'No';
  const debounceTimer = 500;
  const [saveInstance] = useSaveInstanceMutation();
  const [saveBaseStructure] = useSaveBaseStructureMutation();
  const [saveLevels] = useSaveLevelsMutation();
  const [saveSections] = useSaveSectionsMutation();
  const [saveSpaces] = useSaveSpacesMutation();
  const [deleteLevel] = useDeleteLevelMutation();
  const [deleteSection] = useDeleteSectionMutation();
  const [deleteSpace] = useDeleteSpaceMutation();
  const [generateSizingItemsAndAttributes] =
    useGenerateSizingItemsAndAttributesMutation();
  const [generateWindows] = useGenerateWindowsMutation();
  const [saveConfiguration] = useSaveConfigurationMutation();
  const [saveConfigurationCondensers] =
    useSaveConfigurationCondensersMutation();
  const [saveConfigurationZones] = useSaveConfigurationZonesMutation();
  const [saveConfigurationAirHandlers] =
    useSaveConfigurationAirHandlersMutation();
  const [saveConfigurationLineSets] = useSaveConfigurationLineSetsMutation();
  const [deleteConfiguration] = useDeleteConfigurationMutation();
  const [deleteConfigurationCondensers] =
    useDeleteConfigurationCondensersMutation();
  const [deleteConfigurationZones] = useDeleteConfigurationZonesMutation();
  const [deleteConfigurationAirHandlers] =
    useDeleteConfigurationAirHandlersMutation();
  const [deleteConfigurationLineSets] =
    useDeleteConfigurationLineSetsMutation();
  const previousStructureRef = useRef<LevelType[]>(structure);
  const persistedSectionIdsRef = useRef<Set<string>>(new Set());
  const abandonedSectionIdsRef = useRef<Set<string>>(new Set());
  const structureSaveFailuresRef = useRef<Map<string, number>>(new Map());
  const previousConfigurationsRef = useRef<ConfigurationType[]>(configurations);
  const hasPendingDebounceRef = useRef(false);
  // Publish "a save is pending" to the store so the valid-systems watcher
  // can hold its full request until the rows the server scores against
  // have actually landed (see ValidSystemsUpdater). One tracker per save
  // pipeline: the two settle independently.
  const structureSaveTrackerRef = useRef<SaveTracker | null>(null);
  if (structureSaveTrackerRef.current === null) {
    structureSaveTrackerRef.current = createSaveTracker(dispatch, {
      requested: markStructureSaveRequested,
      completed: markStructureSaveCompleted
    });
  }
  const configSaveTrackerRef = useRef<SaveTracker | null>(null);
  if (configSaveTrackerRef.current === null) {
    configSaveTrackerRef.current = createSaveTracker(dispatch, {
      requested: markConfigSaveRequested,
      completed: markConfigSaveCompleted
    });
  }
  // Last SAVED equipment derivation key (AHA-6681 QA F2): compared after
  // every successful saveBaseStructure to decide whether the save changed
  // keep/replace flags, the component set, or the structure subtype — only
  // then does the strips' post-save context-refresh nonce bump. Null until
  // the first save of the session; the null-vs-key first-save bump costs
  // one harmless reconcile GET and guarantees a flip folded into the very
  // first save still refreshes.
  const lastSavedEquipmentKeyRef = useRef<string | null>(null);
  const {
    queueSave,
    hasPendingOperations,
    isBusy: isStructureSaveBusy
  } = useStructureSaveQueue();

  const instanceUniqueIdRef = useRef(instanceUniqueId);
  const instanceCreationPromiseRef = useRef<Promise<string | undefined> | null>(
    null
  );

  useEffect(() => {
    instanceUniqueIdRef.current = instanceUniqueId;
    instanceCreationPromiseRef.current = null;
  }, [instanceUniqueId]);

  const structureUniqueIdRef = useRef(structureUniqueId);
  structureUniqueIdRef.current = structureUniqueId;
  // Minted once so concurrent first-saves upsert the same row. Cleared
  // when the store has a real id so the next lazy create starts fresh.
  const mintedIdRef = useRef<string | null>(null);
  if (structureUniqueId) {
    mintedIdRef.current = null;
  }

  useEffect(() => {
    persistedSectionIdsRef.current = new Set();
    abandonedSectionIdsRef.current = new Set();
    structureSaveFailuresRef.current = new Map();
  }, [structureUniqueId]);

  const saveInstanceData = useCallback(
    async (payload: Partial<SystemSelectorInstanceType>): Promise<void> => {
      const projectId = projectDetails?.projectId;
      if (!projectId) return;

      const persist = (uniqueId?: string) =>
        saveInstance({
          uniqueId,
          projectId,
          structureUniqueId: structureUniqueId ?? undefined,
          ...payload
        }).unwrap();

      const adoptInstanceId = (uniqueId?: string) => {
        if (!uniqueId || uniqueId === instanceUniqueIdRef.current) return;
        instanceUniqueIdRef.current = uniqueId;
        dispatch(setInstanceUniqueId(uniqueId));
      };

      if (instanceUniqueIdRef.current) {
        const result = await persist(instanceUniqueIdRef.current);
        adoptInstanceId(result.instanceUniqueId);
        return;
      }

      if (!instanceCreationPromiseRef.current) {
        instanceCreationPromiseRef.current = persist()
          .then((result) => {
            adoptInstanceId(result.instanceUniqueId);
            return result.instanceUniqueId;
          })
          .catch((error) => {
            instanceCreationPromiseRef.current = null;
            throw error;
          });
        await instanceCreationPromiseRef.current;
        return;
      }

      const createdId = await instanceCreationPromiseRef.current;
      const effectiveId = instanceUniqueIdRef.current ?? createdId;
      const result = await persist(effectiveId);
      adoptInstanceId(result.instanceUniqueId);
    },
    [projectDetails, structureUniqueId, saveInstance, dispatch]
  );

  const configSignature = useMemo(
    () =>
      JSON.stringify(
        configurations.map((config) => ({
          ...config,
          condensers: config.condensers.map((cond) => ({
            ...cond,
            validSystems: [],
            validDuctedSystems: [],
            validSystemsFetched: undefined
          }))
        }))
      ),
    [configurations]
  );

  const systemsToCompareSignature = useMemo(
    () => JSON.stringify(systemsToCompare),
    [systemsToCompare]
  );

  const attomDataSignature = useMemo(
    () => JSON.stringify(attomData),
    [attomData]
  );

  const projectSettingsSignature = useMemo(
    () => JSON.stringify(projectSettings),
    [projectSettings]
  );

  // Property data
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (projectDetails?.projectId) {
      timer = setTimeout(async () => {
        try {
          await saveInstanceData({ propertyData: attomData });

          notification('Saved', 'success');
        } catch {
          notification('Error saving property data', 'error');
        }
      }, debounceTimer);
    }

    return () => clearTimeout(timer);
  }, [attomDataSignature]);

  // Location data
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (!loading && projectDetails?.projectId) {
      timer = setTimeout(async () => {
        try {
          await saveInstanceData({ locationData });

          notification('Saved', 'success');
        } catch {
          notification('Error saving location data', 'error');
        }
      }, debounceTimer);
    }

    return () => clearTimeout(timer);
  }, [locationData]);

  // Property summary
  const lastSavedSummaryRef = useRef<{
    instanceId: string | null;
    value: string | null;
  }>({ instanceId: null, value: null });

  useEffect(() => {
    if (loading) return undefined;
    if (!instanceUniqueId) return undefined;

    if (lastSavedSummaryRef.current.instanceId !== instanceUniqueId) {
      lastSavedSummaryRef.current = {
        instanceId: instanceUniqueId,
        value: propertySummary
      };
      return undefined;
    }

    if (!propertySummary) return undefined;
    if (propertySummary === 'Loading...') return undefined;
    if (propertySummary === 'Failed to generate property summary')
      return undefined;
    if (lastSavedSummaryRef.current.value === propertySummary) return undefined;

    const timer = setTimeout(async () => {
      try {
        await axios.patch('instance/data', {
          instanceUniqueId,
          entries: [{ code: 'property-summary', value: propertySummary }]
        });
        lastSavedSummaryRef.current = {
          instanceId: instanceUniqueId,
          value: propertySummary
        };
      } catch {
        notification('Error saving property summary', 'error');
      }
    }, debounceTimer);

    return () => clearTimeout(timer);
  }, [propertySummary, instanceUniqueId, loading]);

  // Zone duct mode selections
  const lastSavedZoneDuctModesRef = useRef<{
    instanceId: string | null;
    value: string | null;
  }>({ instanceId: null, value: null });

  const zoneDuctModesSignature = useMemo(
    () => serializeZoneDuctModes(zoneDuctModes),
    [zoneDuctModes]
  );

  useEffect(() => {
    if (loading) return undefined;
    if (!instanceUniqueId) return undefined;

    if (lastSavedZoneDuctModesRef.current.instanceId !== instanceUniqueId) {
      lastSavedZoneDuctModesRef.current = {
        instanceId: instanceUniqueId,
        value: zoneDuctModesSignature
      };
      return undefined;
    }

    if (lastSavedZoneDuctModesRef.current.value === zoneDuctModesSignature)
      return undefined;

    const timer = setTimeout(async () => {
      try {
        await axios.patch('instance/data', {
          instanceUniqueId,
          entries: [{ code: 'zone-duct-modes', value: zoneDuctModesSignature }]
        });
        lastSavedZoneDuctModesRef.current = {
          instanceId: instanceUniqueId,
          value: zoneDuctModesSignature
        };
      } catch {
        notification('Error saving duct mode selections', 'error');
      }
    }, debounceTimer);

    return () => clearTimeout(timer);
  }, [zoneDuctModesSignature, instanceUniqueId, loading]);

  // Project settings
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (!loading && projectDetails?.projectId) {
      timer = setTimeout(async () => {
        try {
          await saveInstanceData({
            projectSettings: truncateAttributes(projectSettings)
          });

          notification('Saved', 'success');

          // Save-completion signal (AHA-6796): consumers refetching against
          // saved settings (the confidence score) key on this nonce. Never
          // bumped on failure — the DB is unchanged.
          dispatch(bumpSettingsSaveVersion());
        } catch {
          notification('Error saving project settings', 'error');
        }
      }, debounceTimer);
    }

    return () => clearTimeout(timer);
  }, [projectSettingsSignature]);

  // Structure items
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    // Pending from the moment the save is scheduled until the queued
    // operation settles (or the debounce is cancelled before it fires).
    let scheduledSaveSeq: number | null = null;
    let saveStarted = false;
    const tracker = structureSaveTrackerRef.current;
    if (!loading && projectDetails?.projectId && structureUniqueId && tracker) {
      const previousStructure = previousStructureRef.current;
      const saveSeq = tracker.request();
      scheduledSaveSeq = saveSeq;

      const detectChanges = () => {
        const changedItems: {
          levels: LevelType[];
          sections: Array<
            SectionType & { levelUniqueId: string; floorNum: number }
          >;
          spaces: Array<
            SpaceType & {
              sectionUniqueId: string;
              sizingAttributesChanged: boolean;
            }
          >;
        } = {
          levels: [],
          sections: [],
          spaces: []
        };

        const deletedItems: {
          levelUniqueIds: string[];
          sectionUniqueIds: string[];
          spaceUniqueIds: string[];
        } = {
          levelUniqueIds: [],
          sectionUniqueIds: [],
          spaceUniqueIds: []
        };

        const currentLevelIds = new Set(structure.map((l) => l.uniqueId));
        const currentSectionIds = new Set(
          structure.flatMap((l) => l.sections.map((s) => s.uniqueId))
        );
        const currentSpaceIds = new Set(
          structure.flatMap((l) =>
            l.sections.flatMap((s) => s.spaces.map((sp) => sp.uniqueId))
          )
        );

        previousStructure.forEach((prevLevel) => {
          if (!currentLevelIds.has(prevLevel.uniqueId)) {
            deletedItems.levelUniqueIds.push(prevLevel.uniqueId);
          }

          prevLevel.sections.forEach((prevSection) => {
            if (!currentSectionIds.has(prevSection.uniqueId)) {
              deletedItems.sectionUniqueIds.push(prevSection.uniqueId);
            }

            prevSection.spaces.forEach((prevSpace) => {
              if (!currentSpaceIds.has(prevSpace.uniqueId)) {
                deletedItems.spaceUniqueIds.push(prevSpace.uniqueId);
              }
            });
          });
        });

        structure.forEach((level, levelIndex) => {
          const prevLevel = previousStructure.find(
            (l) => l.uniqueId === level.uniqueId
          );

          if (
            !prevLevel ||
            level.label !== prevLevel.label ||
            level.type !== prevLevel.type ||
            level.conditioned !== prevLevel.conditioned ||
            JSON.stringify(level.attributes) !==
              JSON.stringify(prevLevel.attributes) ||
            JSON.stringify(level.electricalPanels) !==
              JSON.stringify(prevLevel.electricalPanels) ||
            JSON.stringify(level.footprint) !==
              JSON.stringify(prevLevel.footprint) ||
            JSON.stringify(level.footprintShapes ?? null) !==
              JSON.stringify(prevLevel.footprintShapes ?? null) ||
            JSON.stringify(level.suggestedSpaces) !==
              JSON.stringify(prevLevel.suggestedSpaces)
          ) {
            changedItems.levels.push(level);
          }

          level.sections.forEach((section) => {
            const prevSection = prevLevel?.sections.find(
              (s) => s.uniqueId === section.uniqueId
            );

            if (
              !prevSection ||
              section.label !== prevSection.label ||
              JSON.stringify(section.attributes) !==
                JSON.stringify(prevSection.attributes)
            ) {
              changedItems.sections.push({
                ...section,
                levelUniqueId: level.uniqueId,
                floorNum: levelIndex
              });
            }

            section.spaces.forEach((space) => {
              const prevSpace = prevSection?.spaces?.find(
                (s) => s.uniqueId === space.uniqueId
              );

              const sizingAttributesChanged =
                !prevSpace ||
                JSON.stringify(space.sizingAttributes) !==
                  JSON.stringify(prevSpace.sizingAttributes);

              if (
                !prevSpace ||
                space.label !== prevSpace.label ||
                space.itemId !== prevSpace.itemId ||
                space.length !== prevSpace.length ||
                space.width !== prevSpace.width ||
                JSON.stringify(space.openTo) !==
                  JSON.stringify(prevSpace.openTo) ||
                space.conditioned !== prevSpace.conditioned ||
                JSON.stringify(space.position) !==
                  JSON.stringify(prevSpace.position) ||
                JSON.stringify(space.vertices) !==
                  JSON.stringify(prevSpace.vertices) ||
                JSON.stringify(space.attributes) !==
                  JSON.stringify(prevSpace.attributes) ||
                sizingAttributesChanged ||
                JSON.stringify(space.additionalBTUs) !==
                  JSON.stringify(prevSpace.additionalBTUs)
              ) {
                changedItems.spaces.push({
                  ...space,
                  sectionUniqueId: section.uniqueId,
                  sizingAttributesChanged
                });
              }
            });
          });
        });

        return { changedItems, deletedItems };
      };

      hasPendingDebounceRef.current = true;
      timer = setTimeout(() => {
        hasPendingDebounceRef.current = false;
        saveStarted = true;
        const persistStructureItems = async () => {
          const failedIds: string[] = [];
          let failedDeleteIds: Required<StructureItemIds> | null = null;
          const savedLevelIds: string[] = [];
          const savedSectionIds: string[] = [];
          const savedSpaceIds: string[] = [];
          try {
            const { changedItems, deletedItems } = detectChanges();
            let savedCount = 0;
            let deletedCount = 0;

            if (deletedItems.spaceUniqueIds.length > 0) {
              failedIds.push(...deletedItems.spaceUniqueIds);
              failedDeleteIds = {
                levels: [],
                sections: [],
                spaces: deletedItems.spaceUniqueIds
              };
              await deleteSpace({
                spaceUniqueIds: deletedItems.spaceUniqueIds,
                structureUniqueId
              }).unwrap();
              failedIds.length = 0;
              failedDeleteIds = null;
              deletedCount += deletedItems.spaceUniqueIds.length;
              console.log(
                `Deleted ${deletedItems.spaceUniqueIds.length} space(s)`
              );
            }

            if (deletedItems.sectionUniqueIds.length > 0) {
              failedIds.push(...deletedItems.sectionUniqueIds);
              failedDeleteIds = {
                levels: [],
                sections: deletedItems.sectionUniqueIds,
                spaces: []
              };
              await deleteSection({
                sectionUniqueIds: deletedItems.sectionUniqueIds,
                structureUniqueId
              }).unwrap();
              failedIds.length = 0;
              failedDeleteIds = null;
              deletedItems.sectionUniqueIds.forEach((id) => {
                persistedSectionIdsRef.current.delete(id);
                abandonedSectionIdsRef.current.delete(id);
              });
              deletedCount += deletedItems.sectionUniqueIds.length;
              console.log(
                `Deleted ${deletedItems.sectionUniqueIds.length} section(s)`
              );
            }

            if (deletedItems.levelUniqueIds.length > 0) {
              failedIds.push(...deletedItems.levelUniqueIds);
              failedDeleteIds = {
                levels: deletedItems.levelUniqueIds,
                sections: [],
                spaces: []
              };
              await deleteLevel({
                levelUniqueIds: deletedItems.levelUniqueIds,
                structureUniqueId
              }).unwrap();
              failedIds.length = 0;
              failedDeleteIds = null;
              deletedCount += deletedItems.levelUniqueIds.length;
              console.log(
                `Deleted ${deletedItems.levelUniqueIds.length} level(s)`
              );
            }

            if (changedItems.levels.length > 0) {
              const levelIds = changedItems.levels.map(
                (level) => level.uniqueId
              );
              failedIds.push(...levelIds);
              await saveLevels({
                levels: changedItems.levels.map((level) => {
                  const levelIndex = structure.findIndex(
                    (l) => l.uniqueId === level.uniqueId
                  );
                  return {
                    structureUniqueId,
                    uniqueId: level.uniqueId,
                    type: level.type,
                    label: level.label,
                    floorNum: levelIndex,
                    valid: level.valid,
                    modified: level.modified,
                    conditioned: level.conditioned,
                    electricalPanels: level.electricalPanels,
                    footprint: level.footprint ?? null,
                    footprintShapes: level.footprintShapes ?? null,
                    suggestedSpaces: level.suggestedSpaces ?? [],
                    sections: [],
                    attributes: level.attributes?.map((att) => ({
                      code: att.code,
                      value: att.value
                    }))
                  };
                })
              }).unwrap();
              failedIds.length = 0;
              savedLevelIds.push(...levelIds);
              clearStructureSaveFailures(
                structureSaveFailuresRef.current,
                levelIds
              );
              savedCount += changedItems.levels.length;
            }

            const validSpaces = changedItems.spaces.filter((space) => {
              if (space.position && space.BTUs) {
                return true;
              }
              const isNewSpace = !previousStructure.some((level) =>
                level.sections.some((section) =>
                  section.spaces.some((s) => s.uniqueId === space.uniqueId)
                )
              );
              if (isNewSpace) {
                return Boolean(space.BTUs);
              }
              return Boolean(space.position);
            });

            const spacesFromChangedLevels = spacesOnChangedLevels(
              structure,
              changedItems.levels.map((level) => level.uniqueId)
            );

            // A space may only be sent after its section is known-persisted.
            // Unacknowledged parents are upserted once; already-saved
            // sections are not rewritten on every space edit.
            const spacesNeedingParents = uniqueSpacesById([
              ...validSpaces,
              ...spacesFromChangedLevels
            ]);

            const sectionsToSave = [
              ...changedItems.sections,
              ...collectUnpersistedParentSections(
                structure,
                spacesNeedingParents.map((space) => space.sectionUniqueId),
                {
                  persistedSectionIds: persistedSectionIdsRef.current,
                  abandonedSectionIds: abandonedSectionIdsRef.current,
                  alreadySavingSectionIds: changedItems.sections.map(
                    (section) => section.uniqueId
                  )
                }
              )
            ];

            if (sectionsToSave.length > 0) {
              const sectionIds = sectionsToSave.map(
                (section) => section.uniqueId
              );
              failedIds.push(...sectionIds);
              await saveSections({
                sections: sectionsToSave.map((section) => ({
                  uniqueId: section.uniqueId,
                  levelUniqueId: section.levelUniqueId,
                  label: section.label,
                  valid: section.valid,
                  spaces: [],
                  attributes: section.attributes?.map((att) => ({
                    code: att.code,
                    value: att.value
                  }))
                }))
              }).unwrap();
              failedIds.length = 0;
              savedSectionIds.push(...sectionIds);
              sectionIds.forEach((id) => {
                persistedSectionIdsRef.current.add(id);
                abandonedSectionIdsRef.current.delete(id);
              });
              clearStructureSaveFailures(
                structureSaveFailuresRef.current,
                sectionIds
              );
              savedCount += sectionsToSave.length;
            }

            const spacesToSave = spacesNeedingParents.filter((space) =>
              persistedSectionIdsRef.current.has(space.sectionUniqueId)
            );
            const blockedSpaceIds = validSpaces
              .filter((space) =>
                abandonedSectionIdsRef.current.has(space.sectionUniqueId)
              )
              .map((space) => space.uniqueId);

            if (spacesToSave.length > 0) {
              const spaceIds = spacesToSave.map((space) => space.uniqueId);
              failedIds.push(...spaceIds);
              await saveSpaces({
                spaces: spacesToSave.map((space) => {
                  const base = {
                    uniqueId: space.uniqueId,
                    sectionUniqueId: space.sectionUniqueId,
                    label: space.label,
                    itemId: space.itemId,
                    length: space.length,
                    width: space.width,
                    BTUs: spaceSaveBTUs(space.BTUs),
                    airHandlerRankings: spaceSaveAirHandlerRankings(
                      space.airHandlerRankings
                    ),
                    valid: space.valid,
                    modified: space.modified,
                    openTo: space.openTo,
                    conditioned: space.conditioned,
                    attributes: space.attributes?.map((att) => ({
                      code: att.code,
                      value: att.value
                    })),
                    sizingAttributes: space.sizingAttributesChanged
                      ? space.sizingAttributes
                      : undefined,
                    additionalBTUs: space.additionalBTUs
                  };
                  return {
                    ...base,
                    ...(Array.isArray(space.vertices)
                      ? { vertices: space.vertices }
                      : {}),
                    ...(space.position ? { position: space.position } : {})
                  };
                })
              }).unwrap();
              failedIds.length = 0;
              savedSpaceIds.push(...spaceIds);
              clearStructureSaveFailures(
                structureSaveFailuresRef.current,
                spaceIds
              );
              savedCount += spacesToSave.length;
            }

            if (blockedSpaceIds.length > 0) {
              previousStructureRef.current = mergeItemsIntoStructureBaseline(
                previousStructureRef.current,
                structure,
                { spaces: blockedSpaceIds }
              );
            }

            if (savedCount > 0 || deletedCount > 0) {
              notification('Saved', 'success');
              if (savedCount > 0) {
                console.log(`Saved ${savedCount} structure item(s)`);
              }
              previousStructureRef.current = structure;

              if (
                structureUniqueId &&
                !hasPendingOperations() &&
                !hasPendingDebounceRef.current &&
                shouldGenerateSizingAfterStructureSave({
                  savedSpaceCount: spacesToSave.length,
                  deletedSpaceCount: deletedItems.spaceUniqueIds.length,
                  deletedLevelCount: deletedItems.levelUniqueIds.length,
                  levelTypeChangedCount: countChangedLevelTypes(
                    changedItems.levels,
                    previousStructure
                  )
                })
              ) {
                const sizingResult = await generateSizingItemsAndAttributes({
                  structureUniqueId,
                  generateSizingItems: autoGenerateSizingItems,
                  applySpaceAboveAndBelow: autoApplySpaceAboveAndBelow
                }).unwrap();
                if (sizingResult.affectedSpaceUniqueIds.length > 0) {
                  await refetchStructureSpaces(
                    sizingResult.affectedSpaceUniqueIds,
                    structureUniqueId
                  );
                  previousStructureRef.current =
                    mergeSpacesIntoStructureBaseline(
                      previousStructureRef.current,
                      store.getState().structure.structure,
                      sizingResult.affectedSpaceUniqueIds
                    );
                }

                // One-shot post-autobuild windows pass (AHA-6784): the
                // hybrid autobuild hook armed the marker; now that the
                // built structure's first sizing generation landed the
                // wall surfaces server-side, bulk-generate windows once.
                // Marker + preference are read fresh from the singleton
                // store — the component-scope values are stale closures
                // inside this debounced callback (same pattern as the
                // lazy-creation branch).
                if (
                  store.getState().structure.hybridAutobuildWindowsPending &&
                  isHybridOnlyPreference()
                ) {
                  // Clear BEFORE awaiting so a failure can never re-fire
                  // (one-shot).
                  dispatch(setHybridAutobuildWindowsPending(false));
                  try {
                    const windowsResult = await generateWindows({
                      structureUniqueId,
                      width: HYBRID_AUTOBUILD_TUNABLES.WINDOW_WIDTH_FEET,
                      height: HYBRID_AUTOBUILD_TUNABLES.WINDOW_HEIGHT_FEET
                    }).unwrap();
                    console.debug('[hybrid-autobuild] windows', windowsResult);
                    if (windowsResult.affectedSpaceUniqueIds.length > 0) {
                      await refetchStructureSpaces(
                        windowsResult.affectedSpaceUniqueIds,
                        structureUniqueId
                      );
                      previousStructureRef.current =
                        mergeSpacesIntoStructureBaseline(
                          previousStructureRef.current,
                          store.getState().structure.structure,
                          windowsResult.affectedSpaceUniqueIds
                        );
                    }
                  } catch (error) {
                    // Own catch: a windows failure must never surface the
                    // outer 'Error saving structure' toast — the save
                    // itself succeeded.
                    console.error(
                      '[hybrid-autobuild] windows generation failed',
                      error
                    );
                  }
                }
              }
            }
          } catch (error) {
            const quarantinedIds = quarantineFailedSaveIds(
              structureSaveFailuresRef.current,
              failedIds,
              error
            );
            const failure = describeStructureSaveFailure({
              error,
              failedIds,
              quarantinedIds,
              structure
            });
            console.error('Error saving structure:', error, {
              failedIds,
              quarantinedIds,
              details: failure.details
            });
            if (failedDeleteIds) {
              const quarantinedDeletes = {
                levels: failedDeleteIds.levels.filter((id) =>
                  quarantinedIds.includes(id)
                ),
                sections: failedDeleteIds.sections.filter((id) =>
                  quarantinedIds.includes(id)
                ),
                spaces: failedDeleteIds.spaces.filter((id) =>
                  quarantinedIds.includes(id)
                )
              };
              previousStructureRef.current = omitItemsFromStructureBaseline(
                previousStructureRef.current,
                quarantinedDeletes
              );
            } else {
              const quarantined = classifyStructureItemIds(
                structure,
                quarantinedIds
              );
              quarantined.sections.forEach((id) => {
                abandonedSectionIdsRef.current.add(id);
              });
              previousStructureRef.current = mergeItemsIntoStructureBaseline(
                previousStructureRef.current,
                structure,
                {
                  levels: [...savedLevelIds, ...quarantined.levels],
                  sections: [...savedSectionIds, ...quarantined.sections],
                  spaces: [
                    ...savedSpaceIds,
                    ...quarantined.spaces,
                    ...spaceUniqueIdsWithAbandonedParents(
                      structure,
                      abandonedSectionIdsRef.current
                    )
                  ]
                }
              );
            }
            notification(
              failure.message,
              'error',
              failure.blocking ? BLOCKING_SAVE_FAILURE_TOAST : {}
            );
          }
        };
        // Use the save queue to serialize structure save operations and
        // prevent deadlocks. The queue keeps at most one operation waiting:
        // a superseded one never runs, and its sequence is covered by the
        // newer one's completion.
        queueSave(() =>
          persistStructureItems().finally(() => tracker.complete(saveSeq))
        );
      }, debounceTimer);
    }

    return () => {
      clearTimeout(timer);
      hasPendingDebounceRef.current = false;
      if (scheduledSaveSeq !== null && !saveStarted) {
        tracker?.complete(scheduledSaveSeq);
      }
    };
  }, [structure, structureUniqueId]);

  const persistBaseStructure = useCallback(async () => {
    const { project, structure: structureState } = store.getState();
    const projectId = project.projectDetails?.projectId;
    if (!projectId) {
      return;
    }

    try {
      const persistStructure = (uniqueId: string) =>
        saveBaseStructure({
          projectId,
          addressId: project.addressId ?? undefined,
          structure: {
            uniqueId,
            name: structureState.structureName ?? null,
            typeId: structureState.structureTypeId ?? null,
            subtypeId: structureState.structureSubtypeId ?? null,
            isPartial: structureState.partialStructure ?? null,
            notes: structureState.notes ?? null,
            existingEquipment: structureState.existingSystems,
            footprint: structureState.footprint ?? null,
            condenserLocations: structureState.condenserLocations ?? null
          }
        }).unwrap();

      const adoptCreatedStructure = (createdUniqueId: string) => {
        structureUniqueIdRef.current = createdUniqueId;
        dispatch(setStructureUniqueId(createdUniqueId));
        // Lazily created structure — flag it so the next ATTOM-driven
        // insulation seed persists fresh year-built defaults, and when
        // ATTOM data already arrived (that trigger has come and gone)
        // re-run the seed now so the fresh flag actually persists them.
        // When the ATTOM location chain is still in flight
        // (isLocationDataPending), skip the immediate seed and leave
        // isNewlyCreatedStructure armed so the in-flight callback (or
        // its catch fallback) persists with the best available zone —
        // exactly one persist-seed wins.
        // Reads the singleton store: the component-scope attomData /
        // locationData are stale closures inside this debounced
        // callback. Deliberately not awaited (fire-and-forget like the
        // rest of the callback) — the seed never rejects by contract.
        dispatch(setIsNewlyCreatedStructure(true));
        const {
          attomData: currentAttomData,
          locationData: currentLocationData,
          isLocationDataPending
        } = store.getState().project;
        if (currentAttomData && !isLocationDataPending) {
          seedDefaultSizingItemInsulationValues(
            currentAttomData,
            currentLocationData?.climateZone ?? '4'
          );
        }
        setQueryParam('structureId', createdUniqueId);

        dispatch(
          setStructureOptions([
            ...store.getState().structure.structureOptions,
            {
              label: `Unnamed Structure (${createdUniqueId.slice(-5)})`,
              uniqueId: createdUniqueId
            }
          ])
        );

        console.log('Setting structure ID to', createdUniqueId);
      };

      const uniqueId =
        structureUniqueIdRef.current ?? (mintedIdRef.current ??= nanoid(5));
      const result = await persistStructure(uniqueId);
      if (!structureUniqueIdRef.current) {
        adoptCreatedStructure(result.structureUniqueId ?? uniqueId);
      }
      const savedUniqueId = structureUniqueIdRef.current ?? uniqueId;

      notification('Saved', 'success');

      console.log(`Saved structure metadata (${savedUniqueId})`);

      // Post-save context refresh (AHA-6681 QA F2): a keep/replace flip
      // or component add/remove only reaches the DB when THIS debounced
      // save lands, so the strips' flip-triggered context GET derived
      // from pre-save state (fingerprint unchanged → no reseed). Bump
      // the version only when the saved payload's derivation key
      // actually changed — name/notes/footprint-only saves must not
      // refire the strips. A structure-subtype flip must also refire
      // them: the server dwelling swap it drives sits upstream of the
      // derivation fingerprint (cursor[bot] PR #4156 finding). The
      // post-save timing is what avoids the race — the refired GET
      // reads the persisted new subtype. Never bumped on failure: the
      // DB is unchanged and a refetch would still see the old
      // derivation.
      const equipmentKey = buildEquipmentDerivationKey(
        structureState.existingSystems,
        structureState.structureSubtypeId ?? null
      );
      if (lastSavedEquipmentKeyRef.current !== equipmentKey) {
        lastSavedEquipmentKeyRef.current = equipmentKey;
        dispatch(bumpEquipmentSaveVersion());
      }
      // Unlike the derivation-keyed bump above, the persist nonce moves
      // on EVERY successful save — spec/model edits are excluded from
      // the derivation key but do change the persisted state the
      // confidence score reads (AHA-6796). Never bumped on failure: the
      // DB is unchanged.
      dispatch(bumpEquipmentPersistVersion());
    } catch {
      notification('Error saving structure metadata', 'error');
    }
  }, [saveBaseStructure, dispatch]);

  const persistBaseStructureRef = useRef(persistBaseStructure);
  persistBaseStructureRef.current = persistBaseStructure;
  const baseStructureSaveRunnerRef = useRef<CoalescingAsyncRunner | null>(null);
  if (baseStructureSaveRunnerRef.current === null) {
    baseStructureSaveRunnerRef.current = createCoalescingAsyncRunner(() =>
      persistBaseStructureRef.current()
    );
  }

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (
      !loading &&
      projectDetails?.projectId &&
      (structureName ||
        structureTypeId ||
        structureSubtypeId ||
        partialStructure ||
        notes ||
        existingSystems.length > 0)
    ) {
      timer = setTimeout(() => {
        baseStructureSaveRunnerRef.current?.request();
      }, debounceTimer);
    }

    return () => clearTimeout(timer);
  }, [
    structureName,
    structureTypeId,
    structureSubtypeId,
    partialStructure,
    notes,
    existingSystems,
    footprint,
    condenserLocations
  ]);

  // Configurations
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    // Pending from the moment the save is scheduled until it settles (or is
    // superseded by a newer signature before it starts), so the gate never
    // releases on a stale save and never sticks on a cancelled one.
    let scheduledSaveSeq: number | null = null;
    let saveStarted = false;
    const tracker = configSaveTrackerRef.current;

    if (!loading && projectDetails?.projectId && instanceUniqueId && tracker) {
      const defaultLevelUniqueId = structure[0]?.uniqueId ?? '';
      const saveSeq = tracker.request();
      scheduledSaveSeq = saveSeq;

      const attemptSave = async () => {
        if (isLinesetDragActive()) {
          tracker.complete(saveSeq);
          return;
        }

        const structureState = store.getState().structure;
        const structureRowMissing =
          structureState.structure.length > 0 &&
          !structureState.structureUniqueId;

        if (
          structureRowMissing ||
          hasPendingDebounceRef.current ||
          isStructureSaveBusy()
        ) {
          timer = setTimeout(attemptSave, debounceTimer);
          return;
        }

        saveStarted = true;
        try {
          const changes = detectConfigurationChanges(
            previousConfigurationsRef.current,
            configurations,
            defaultLevelUniqueId
          );

          let savedCount = 0;
          let deletedCount = 0;

          if (changes.deletedAirHandlerUniqueIds.length > 0) {
            await deleteConfigurationAirHandlers({
              airHandlerUniqueIds: changes.deletedAirHandlerUniqueIds
            }).unwrap();
            deletedCount += changes.deletedAirHandlerUniqueIds.length;
            console.log(
              `Deleted ${changes.deletedAirHandlerUniqueIds.length} air handler(s)`
            );
          }

          if (changes.deletedLineSetUniqueIds.length > 0) {
            await deleteConfigurationLineSets({
              lineSetUniqueIds: changes.deletedLineSetUniqueIds
            }).unwrap();
            deletedCount += changes.deletedLineSetUniqueIds.length;
            console.log(
              `Deleted ${changes.deletedLineSetUniqueIds.length} line set(s)`
            );
          }

          if (changes.deletedZoneUniqueIds.length > 0) {
            await deleteConfigurationZones({
              zoneUniqueIds: changes.deletedZoneUniqueIds
            }).unwrap();
            deletedCount += changes.deletedZoneUniqueIds.length;
            console.log(
              `Deleted ${changes.deletedZoneUniqueIds.length} zone(s)`
            );
          }

          if (changes.deletedCondenserUniqueIds.length > 0) {
            await deleteConfigurationCondensers({
              condenserUniqueIds: changes.deletedCondenserUniqueIds
            }).unwrap();
            deletedCount += changes.deletedCondenserUniqueIds.length;
            console.log(
              `Deleted ${changes.deletedCondenserUniqueIds.length} condenser(s)`
            );
          }

          if (changes.deletedConfigurationUniqueIds.length > 0) {
            for (const configUniqueId of changes.deletedConfigurationUniqueIds) {
              await deleteConfiguration(configUniqueId).unwrap();
            }
            deletedCount += changes.deletedConfigurationUniqueIds.length;
            console.log(
              `Deleted ${changes.deletedConfigurationUniqueIds.length} configuration(s)`
            );
          }

          for (const config of changes.changedConfigurations) {
            await saveConfiguration(
              transformConfigurationForDatabase(config, instanceUniqueId)
            ).unwrap();
            savedCount += 1;
          }

          const condensersByConfig = new Map<
            string,
            typeof changes.changedCondensers
          >();
          changes.changedCondensers.forEach((item) => {
            const existing =
              condensersByConfig.get(item.configurationUniqueId) ?? [];
            existing.push(item);
            condensersByConfig.set(item.configurationUniqueId, existing);
          });

          for (const [configUniqueId, condenserItems] of condensersByConfig) {
            const condensersForDb = condenserItems.map(
              (item) =>
                transformCondensersForDatabase(
                  [item.condenser],
                  item.defaultLevelUniqueId
                )[0]
            );
            await saveConfigurationCondensers({
              configurationUniqueId: configUniqueId,
              condensers: condensersForDb
            }).unwrap();
            savedCount += condenserItems.length;
          }

          const zonesByConfig = new Map<
            string,
            (typeof changes)['changedZones']
          >();
          changes.changedZones.forEach((item) => {
            const existing =
              zonesByConfig.get(item.configurationUniqueId) ?? [];
            existing.push(item);
            zonesByConfig.set(item.configurationUniqueId, existing);
          });

          for (const [configUniqueId, zoneItems] of zonesByConfig) {
            const zonesForDb = zoneItems.map((item) => ({
              uniqueId: item.zone.uniqueId,
              condenserUniqueId: item.condenserUniqueId,
              label: item.zone.name,
              zoneTypeCode: zoneTypeCodeForSave(item.zone),
              existingSystemUniqueId: item.zone.existingSystemId ?? null,
              valid: item.zone.valid ?? false,
              modified: item.zone.modified ?? false,
              spaceUniqueIds: item.zone.zoneItems.map(
                (zi) => zi.structureItemId
              ),
              spaceItems: item.zone.zoneItems.map((zi) => ({
                structureItemId: zi.structureItemId,
                registerOverride: zi.registerOverride ?? null,
                additionalSupplyRuns: zi.additionalSupplyRuns ?? [],
                supplyRunPoints: zi.supplyRunPoints ?? [],
                servePassively: zi.servePassively ?? null
              })),
              returnOverride: item.zone.returnOverride ?? null,
              additionalReturnRuns: item.zone.additionalReturnRuns ?? [],
              returnRunPoints: item.zone.returnRunPoints ?? []
            }));
            await saveConfigurationZones({
              configurationUniqueId: configUniqueId,
              zones: zonesForDb
            }).unwrap();
            savedCount += zoneItems.length;
          }

          const airHandlersByConfig = new Map<
            string,
            typeof changes.changedAirHandlers
          >();
          changes.changedAirHandlers.forEach((item) => {
            const existing =
              airHandlersByConfig.get(item.configurationUniqueId) ?? [];
            existing.push(item);
            airHandlersByConfig.set(item.configurationUniqueId, existing);
          });

          for (const [configUniqueId, ahItems] of airHandlersByConfig) {
            const airHandlersForDb = ahItems
              .filter((item) => item.zone.airHandler)
              .map((item) => ({
                uniqueId: item.zone.airHandler!.uniqueId,
                zoneUniqueId: item.zone.uniqueId,
                airHandlerTypeCode: item.zone.airHandler!.airHandlerTypeCode,
                positionX: item.zone.airHandler!.positionX,
                positionY: item.zone.airHandler!.positionY,
                valid: item.zone.airHandler!.valid ?? false,
                modified: item.zone.airHandler!.modified ?? false,
                userPositioned: item.zone.airHandler!.userPositioned,
                concealedLocation: item.zone.airHandler!.concealedLocation
              }));
            if (airHandlersForDb.length > 0) {
              await saveConfigurationAirHandlers({
                configurationUniqueId: configUniqueId,
                airHandlers: airHandlersForDb
              }).unwrap();
              savedCount += airHandlersForDb.length;
            }
          }

          const lineSetsByConfig = new Map<
            string,
            typeof changes.changedLineSets
          >();
          changes.changedLineSets.forEach((item) => {
            const existing =
              lineSetsByConfig.get(item.configurationUniqueId) ?? [];
            existing.push(item);
            lineSetsByConfig.set(item.configurationUniqueId, existing);
          });

          for (const [configUniqueId, lsItems] of lineSetsByConfig) {
            const config = configurations.find(
              (c) => c.uniqueId === configUniqueId
            );
            if (config) {
              const referencedAirHandlerIds = new Set(
                lsItems.map((item) => item.lineSet.airHandlerUniqueId)
              );
              const referencedCondenserIds = new Set(
                lsItems.map((item) => item.lineSet.condenserUniqueId)
              );

              const condensersToEnsure: Array<{
                condenser: CondenserType;
                defaultLevelUniqueId: string;
              }> = [];
              const zonesToEnsure: Array<{
                zone: ZoneType;
                condenserUniqueId: string;
              }> = [];
              const airHandlersToEnsure: Array<{
                uniqueId: string;
                zoneUniqueId: string;
                airHandlerTypeCode: AirHandlerTypeCodeType;
                positionX: number;
                positionY: number;
                valid: boolean;
                modified: boolean;
                userPositioned?: boolean | null;
                concealedLocation?: ConcealedDuctLocationType | null;
              }> = [];

              const condenserUniqueIdsToEnsure = new Set<string>();

              config.condensers.forEach((condenser) => {
                if (referencedCondenserIds.has(condenser.uniqueId)) {
                  condenserUniqueIdsToEnsure.add(condenser.uniqueId);
                }

                condenser.zones.forEach((zone) => {
                  if (
                    zone.airHandler &&
                    referencedAirHandlerIds.has(zone.airHandler.uniqueId)
                  ) {
                    condenserUniqueIdsToEnsure.add(condenser.uniqueId);
                    zonesToEnsure.push({
                      zone,
                      condenserUniqueId: condenser.uniqueId
                    });
                    airHandlersToEnsure.push({
                      uniqueId: zone.airHandler.uniqueId,
                      zoneUniqueId: zone.uniqueId,
                      airHandlerTypeCode: zone.airHandler.airHandlerTypeCode,
                      positionX: zone.airHandler.positionX,
                      positionY: zone.airHandler.positionY,
                      valid: zone.airHandler.valid ?? false,
                      modified: zone.airHandler.modified ?? false,
                      userPositioned: zone.airHandler.userPositioned,
                      concealedLocation: zone.airHandler.concealedLocation
                    });
                  }
                });
              });

              config.condensers.forEach((condenser) => {
                if (condenserUniqueIdsToEnsure.has(condenser.uniqueId)) {
                  condensersToEnsure.push({
                    condenser,
                    defaultLevelUniqueId
                  });
                }
              });

              if (condensersToEnsure.length > 0) {
                const condensersForDb = condensersToEnsure.map(
                  (item) =>
                    transformCondensersForDatabase(
                      [item.condenser],
                      item.defaultLevelUniqueId
                    )[0]
                );
                await saveConfigurationCondensers({
                  configurationUniqueId: configUniqueId,
                  condensers: condensersForDb
                }).unwrap();
              }

              if (zonesToEnsure.length > 0) {
                const zonesForDb = zonesToEnsure.map((item) => ({
                  uniqueId: item.zone.uniqueId,
                  condenserUniqueId: item.condenserUniqueId,
                  label: item.zone.name,
                  zoneTypeCode: zoneTypeCodeForSave(item.zone),
                  existingSystemUniqueId: item.zone.existingSystemId ?? null,
                  valid: item.zone.valid ?? false,
                  modified: item.zone.modified ?? false,
                  spaceUniqueIds: item.zone.zoneItems.map(
                    (zi) => zi.structureItemId
                  ),
                  spaceItems: item.zone.zoneItems.map((zi) => ({
                    structureItemId: zi.structureItemId,
                    registerOverride: zi.registerOverride ?? null,
                    additionalSupplyRuns: zi.additionalSupplyRuns ?? [],
                    supplyRunPoints: zi.supplyRunPoints ?? [],
                    servePassively: zi.servePassively ?? null
                  })),
                  returnOverride: item.zone.returnOverride ?? null,
                  additionalReturnRuns: item.zone.additionalReturnRuns ?? [],
                  returnRunPoints: item.zone.returnRunPoints ?? []
                }));
                await saveConfigurationZones({
                  configurationUniqueId: configUniqueId,
                  zones: zonesForDb
                }).unwrap();
              }

              if (airHandlersToEnsure.length > 0) {
                await saveConfigurationAirHandlers({
                  configurationUniqueId: configUniqueId,
                  airHandlers: airHandlersToEnsure
                }).unwrap();
              }

              const lineSetsForDb = transformLineSetsForDatabase(
                lsItems.map((item) => item.lineSet)
              );
              if (lineSetsForDb.length > 0) {
                await saveConfigurationLineSets({
                  configurationUniqueId: configUniqueId,
                  lineSets: lineSetsForDb
                }).unwrap();
                savedCount += lineSetsForDb.length;
              }
            }
          }

          if (changes.changedConfigurations.length > 0) {
            dispatch(
              markLocallyCreatedConfigurationIdsSaved(
                changes.changedConfigurations.map((config) => config.uniqueId)
              )
            );
          }

          if (savedCount > 0 || deletedCount > 0) {
            notification('Saved', 'success');
            console.log(
              `Saved ${savedCount} configuration item(s), deleted ${deletedCount}`
            );
            previousConfigurationsRef.current = configurations;
          }

          dispatch(setConfigSavedAt(Date.now()));
        } catch (error) {
          console.error('Error saving configurations:', error);
          notification('Error saving configurations', 'error');
        } finally {
          tracker.complete(saveSeq);
        }
      };

      timer = setTimeout(attemptSave, debounceTimer);
    }

    return () => {
      clearTimeout(timer);
      if (scheduledSaveSeq !== null && !saveStarted) {
        tracker?.complete(scheduledSaveSeq);
      }
    };
  }, [configSignature, instanceUniqueId]);

  // System filters
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (!loading && projectDetails?.projectId) {
      timer = setTimeout(async () => {
        try {
          await saveInstanceData({ systemFilters });

          notification('Saved', 'success');
        } catch {
          notification('Error saving system filters', 'error');
        }
      }, debounceTimer);
    }

    return () => clearTimeout(timer);
  }, [systemFilters]);

  // Selected systems
  const selectedSystemsSignature = useMemo(
    () => JSON.stringify(selectedSystems.map(stripQuoteOnlyFields)),
    [selectedSystems]
  );
  const selectedDuctedSystemsSignature = useMemo(
    () => JSON.stringify(selectedDuctedSystems.map(stripQuoteOnlyFields)),
    [selectedDuctedSystems]
  );

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (!loading && projectDetails?.projectId) {
      timer = setTimeout(async () => {
        try {
          await saveInstanceData({
            selectedSystems: {
              ductless: truncateSelectedSystems(selectedSystems),
              ducted: truncateSelectedSystems(selectedDuctedSystems)
            },
            systemsToCompare: sanitizeSystemsToCompare(systemsToCompare)
          });

          notification('Saved', 'success');
        } catch {
          notification('Error saving selected systems', 'error');
        }
      }, debounceTimer);
    }

    return () => clearTimeout(timer);
  }, [
    selectedSystemsSignature,
    selectedDuctedSystemsSignature,
    systemsToCompareSignature
  ]);

  // Default sizing items
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log(
        'defaultSizingItems changed',
        detailedDiff(defaultSizingItems, previousDefaultSizingItemsRef.current)
      );
    }
    previousDefaultSizingItemsRef.current = defaultSizingItems;

    let timer: ReturnType<typeof setTimeout>;
    if (!loading && projectDetails?.projectId && structureUniqueId) {
      if (!areAllDefaultSizingItemsValid(defaultSizingItems)) {
        notification(
          'Cannot save sizing defaults - some values are invalid',
          'warning'
        );
        return undefined;
      }

      timer = setTimeout(async () => {
        try {
          await dispatch(
            saveStructureSpecificSizingItemDefaults({
              structureUniqueId,
              updates: [
                {
                  sizingItemTypeCode: 'ceilingBelowAttic',
                  defaults: defaultSizingItems.ceilingBelowAttic
                },
                {
                  sizingItemTypeCode: 'ceilingBelowRoofJoists',
                  defaults: {
                    ...defaultSizingItems.ceilingBelowRoofJoists,
                    skylights: undefined
                  }
                },
                {
                  sizingItemTypeCode: 'ceilingOnExposedBeams',
                  defaults: {
                    ...defaultSizingItems.ceilingOnExposedBeams,
                    skylights: undefined
                  }
                },
                {
                  sizingItemTypeCode: 'floorOverUnconditionedSpace',
                  defaults: defaultSizingItems.floorOverUnconditionedSpace
                },
                {
                  sizingItemTypeCode: 'floorOverExposedSpace',
                  defaults: defaultSizingItems.floorOverExposedSpace
                },
                {
                  sizingItemTypeCode: 'aboveGradeWall',
                  defaults: {
                    ...defaultSizingItems.aboveGradeWall,
                    verticalGlass: undefined,
                    solidDoors: undefined
                  }
                },
                {
                  sizingItemTypeCode: 'belowGradeWall',
                  defaults: {
                    ...defaultSizingItems.belowGradeWall,
                    verticalGlass: undefined,
                    solidDoors: undefined
                  }
                },
                {
                  sizingItemTypeCode: 'slabFloor',
                  defaults: defaultSizingItems.slabFloor
                },
                {
                  sizingItemTypeCode: 'basementFloor',
                  defaults: defaultSizingItems.basementFloor
                },
                {
                  sizingItemTypeCode: 'verticalGlass',
                  defaults: defaultSizingItems.verticalGlass
                },
                {
                  sizingItemTypeCode: 'skylight',
                  defaults: defaultSizingItems.skylight
                },
                {
                  sizingItemTypeCode: 'solidDoor',
                  defaults: defaultSizingItems.solidDoor
                }
              ]
            })
          ).unwrap();
          notification('Saved', 'success');
        } catch (error) {
          console.error('Error saving default sizing items:', error);
          notification('Error saving default sizing items', 'error');
        }
      }, debounceTimer);
    }

    return () => clearTimeout(timer);
  }, [defaultSizingItemsVersion]);

  return null;
}
